import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiMail, FiLock, FiEye, FiEyeOff, FiUser } from 'react-icons/fi';
import { FaGoogle, FaApple } from 'react-icons/fa';
import Navbar from '../components/Navbar';
import { authAPI } from '../services/api';
import { useAuthContext } from '../context/AuthContext';
import { useGoogleLogin } from '@react-oauth/google';

import logger from '../utils/logger';

const Register = () => {
  const navigate = useNavigate();
  const { login } = useAuthContext();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
    preferredSport: 'Football',
    acceptedTerms: false,
    acceptedGuidelines: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validate inputs
    if (!formData.fullName || !formData.email || !formData.password || !formData.confirmPassword) {
      const validationError = 'Please fill in all fields (Full Name, Email, Password)';
      setError(validationError);
      logger.warn('Registration validation failed', { reason: 'Missing fields' });
      setLoading(false);
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      const validationError = 'Please enter a valid email address (e.g., user@example.com)';
      setError(validationError);
      logger.warn('Registration validation failed', { reason: 'Invalid email format' });
      setLoading(false);
      return;
    }

    // Password validation - must match backend requirements
    const passwordRegex = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,}$/;
    if (!passwordRegex.test(formData.password)) {
      const validationError = 'Password must contain at least 8 characters with: 1 uppercase letter, 1 lowercase letter, 1 number, and at least 1 special character (!@#$%^&*). Example: Test@1234';
      setError(validationError);
      logger.warn('Registration validation failed', { reason: 'Password does not meet requirements' });
      setLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      const validationError = 'Passwords do not match. Please check and try again.';
      setError(validationError);
      logger.warn('Registration validation failed', { reason: 'Passwords do not match' });
      setLoading(false);
      return;
    }

    if (!formData.acceptedTerms || !formData.acceptedGuidelines) {
      const validationError = 'Please accept the Terms & Conditions and Community Guidelines to continue.';
      setError(validationError);
      logger.warn('Registration validation failed', { reason: 'Legal agreements not accepted' });
      setLoading(false);
      return;
    }


    try {
      logger.info('Attempting user registration', { email: formData.email });
      // Call backend register API
      const response = await authAPI.register({
        fullName: formData.fullName,
        email: formData.email,
        password: formData.password,
        preferredSport: formData.preferredSport.toLowerCase(),
        skillLevel: 'intermediate',
      });

      if (response.success) {
        // Use context login() — updates global state AND localStorage atomically
        login(response.user, response.accessToken, response.refreshToken);

        logger.info('User registration successful', { email: formData.email });
        navigate('/dashboard');
      } else {
        setError(response.message || 'Registration failed');
        logger.warn('Registration failed', { email: formData.email, reason: response.message });
      }
    } catch (err) {
      // Map backend error messages to user-friendly messages
      let errorMessage = err.message || 'Registration failed. Please try again.';
      
      if (errorMessage.includes('Email already registered') || errorMessage.includes('already')) {
        errorMessage = 'This email is already registered. Please try logging in or use a different email.';
      } else if (errorMessage.includes('password') || errorMessage.includes('Password')) {
        errorMessage = 'Password does not meet requirements. Use 8+ characters with uppercase, lowercase, number, and at least one special character (!@#$%^&*)';
      } else if (errorMessage.includes('email') || errorMessage.includes('Email')) {
        errorMessage = 'Invalid email address. Please check and try again.';
      } else if (errorMessage.includes('name') || errorMessage.includes('Name')) {
        errorMessage = 'Invalid name. Please enter your full name.';
      } else if (errorMessage.includes('Invalid response')) {
        errorMessage = 'Server connection issue. Please check your internet and try again.';
      }
      
      setError(errorMessage);
      logger.error('Registration error', { email: formData.email, error: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        setLoading(true);
        setError('');
        // We use the access_token to get user info from Google or send it to our backend
        const response = await authAPI.googleLogin({ accessToken: tokenResponse.access_token });
        
        if (response.success) {
          login(response.user, response.accessToken, response.refreshToken);
          logger.info('Google login successful');
          navigate('/dashboard');
        }
      } catch (err) {
        setError(err.message || 'Google login failed');
        logger.error('Google login error', { error: err.message });
      } finally {
        setLoading(false);
      }
    },
    onError: () => {
      setError('Google Sign In was unsuccessful');
      logger.error('Google login failed at client level');
    },
  });

  const handleGoogleSuccess = async (credentialResponse) => {

    try {
      setLoading(true);
      setError('');
      const response = await authAPI.googleLogin(credentialResponse.credential);
      
      if (response.success) {
        login(response.user, response.accessToken, response.refreshToken);
        logger.info('Google login successful');
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.message || 'Google login failed');
      logger.error('Google login error', { error: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError('Google Sign In was unsuccessful. Try again later');
    logger.error('Google login failed at client level');
  };

  return (
    <div className="bg-black min-h-screen">
      <Navbar hideAuthButtons={true} />

      <div className="flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 min-h-[calc(100vh-64px)]">
        <div className="w-full max-w-2xl">
          <div className="flex items-stretch gap-0">
            {/* Left Red Accent Line */}
            <div className="w-1.5 bg-red-500"></div>
            
            {/* Form Container */}
            <div className="bg-gray-900 p-12 flex-1 rounded-r-lg">
              {/* Header */}
              <div className="mb-8">
                <h1 className="text-5xl font-bold text-red-500 mb-2 tracking-wider">JOIN THE SQUAD</h1>
                <p className="text-gray-400 text-xs font-bold uppercase tracking-widest">
                  Create your account to start playing
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Full Name Field */}
                <div>
                  <label htmlFor="fullName" className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    placeholder="Enter your name"
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white placeholder-gray-600 rounded focus:outline-none focus:border-red-500 transition text-sm"
                  />
                </div>

                {/* Email Field */}
                <div>
                  <label htmlFor="email" className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">
                    Email Address
                  </label>
                  <div className="relative">
                    <FiMail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 w-5 h-5" />
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="you@example.com"
                      className="w-full pl-12 pr-4 py-3 bg-gray-800 border border-gray-700 text-white placeholder-gray-600 rounded focus:outline-none focus:border-red-500 transition text-sm"
                    />
                  </div>
                </div>

                {/* Password Field */}
                <div>
                  <label htmlFor="password" className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">
                    Password
                  </label>
                  <div className="relative">
                    <FiLock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 w-5 h-5" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Create a password"
                      className="w-full pl-12 pr-12 py-3 bg-gray-800 border border-gray-700 text-white placeholder-gray-600 rounded focus:outline-none focus:border-red-500 transition text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-400 transition"
                    >
                      {showPassword ? <FiEyeOff className="w-5 h-5" /> : <FiEye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {/* Confirm Password Field */}
                <div>
                  <label htmlFor="confirmPassword" className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <FiLock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 w-5 h-5" />
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      id="confirmPassword"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="Repeat password"
                      className="w-full pl-12 pr-12 py-3 bg-gray-800 border border-gray-700 text-white placeholder-gray-600 rounded focus:outline-none focus:border-red-500 transition text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-400 transition"
                    >
                      {showConfirmPassword ? <FiEyeOff className="w-5 h-5" /> : <FiEye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {/* Preferred Sport Dropdown */}
                <div>
                  <label htmlFor="sport" className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">
                    Preferred Sport
                  </label>
                  <select
                    id="sport"
                    name="preferredSport"
                    value={formData.preferredSport}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded focus:outline-none focus:border-red-500 transition text-sm appearance-none cursor-pointer"
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23999' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 12px center',
                      paddingRight: '36px',
                    }}
                  >
                    <option value="Football">Football</option>
                    <option value="Basketball">Basketball</option>
                    <option value="Tennis">Tennis</option>
                    <option value="Volleyball">Volleyball</option>
                  </select>
                </div>

                {/* Agreement Checkboxes */}
                <div className="space-y-4 pt-2">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="acceptedTerms"
                      name="acceptedTerms"
                      checked={formData.acceptedTerms}
                      onChange={handleChange}
                      className="mt-1 w-4 h-4 bg-gray-800 border-gray-700 rounded text-red-500 focus:ring-red-500 transition cursor-pointer"
                    />
                    <label htmlFor="acceptedTerms" className="text-xs text-gray-400 leading-tight">
                      I have read and agree to the{' '}
                      <Link to="/terms" className="text-red-500 hover:underline">Terms and Conditions</Link>
                      {' '}and{' '}
                      <Link to="/privacy" className="text-red-500 hover:underline">Privacy Policy</Link>
                    </label>
                  </div>

                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="acceptedGuidelines"
                      name="acceptedGuidelines"
                      checked={formData.acceptedGuidelines}
                      onChange={handleChange}
                      className="mt-1 w-4 h-4 bg-gray-800 border-gray-700 rounded text-red-500 focus:ring-red-500 transition cursor-pointer"
                    />
                    <label htmlFor="acceptedGuidelines" className="text-xs text-gray-400 leading-tight">
                      I agree to follow the{' '}
                      <Link to="/guidelines" className="text-red-500 hover:underline">Community Guidelines</Link>
                    </label>
                  </div>
                </div>


                {/* Error Message */}
                {error && (
                  <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-3">
                    <p className="text-red-400 text-xs">{error}</p>
                  </div>
                )}

                {/* Register Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-red-500 hover:bg-red-600 disabled:bg-red-700 text-white font-bold py-3 px-4 rounded transition duration-200 text-sm uppercase tracking-wider mt-8"
                >
                  {loading ? 'REGISTERING...' : 'REGISTER'}
                </button>
              </form>

              {/* Google Sign In */}
              <div className="relative my-8">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-800"></div>
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-gray-900 px-4 text-gray-500 font-bold tracking-widest">OR JOIN WITH</span>
                </div>
              </div>

              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={() => handleGoogleLogin()}
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-3 bg-white hover:bg-gray-100 text-gray-900 font-bold py-3 px-4 rounded transition duration-200 text-sm uppercase tracking-wider"
                >
                  <FaGoogle className="text-red-500 w-5 h-5" />
                  Join with Google
                </button>
              </div>

                <div className="mt-8 text-center">
                  <p className="text-gray-500 text-xs font-bold uppercase tracking-widest">
                    Already part of the squad?{' '}
                    <Link to="/login" className="text-red-500 hover:text-red-400 transition ml-1">
                      LOGIN HERE
                    </Link>
                  </p>
                </div>
              </div>

            </div>
          </div>
      </div>
    </div>
  );
};

export default Register;
